/**
 * users-service.ts
 * 
 * Service per la gestione degli utenti
 */

import { apiClient } from './api';

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  assignedTasksCount: number;
  completedTasksCount: number;
  createdRequestsCount: number;
}

export enum UserRole {
  Admin = 0,
  CapoUfficioStile = 1,
  UfficioStile = 2,
  Produzione = 3,
  Industrializzazione = 4,
  GestioneOrdini = 5,
  ReadOnly = 6
}

export const UserRoleLabels: Record<UserRole, string> = {
  [UserRole.Admin]: 'Admin',
  [UserRole.CapoUfficioStile]: 'Admin',
  [UserRole.UfficioStile]: 'Ufficio Stile',
  [UserRole.Produzione]: 'Produzione',
  [UserRole.Industrializzazione]: 'Industrializzazione',
  [UserRole.GestioneOrdini]: 'Gestione Ordini',
  [UserRole.ReadOnly]: 'Read Only'
};

export interface CreateUserRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface UserStatistics {
  userId: number;
  totalAssignedTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  totalCreatedRequests: number;
  approvedRequests: number;
  pendingRequests: number;
  rejectedRequests: number;
  averageTaskCompletionTime: number;
  productivityScore: number;
}

export interface UsersPagedResult {
  items: User[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

class UsersService {
  async getAll(
    pageNumber = 1,
    pageSize = 20,
    searchTerm?: string,
    role?: UserRole,
    isActive?: boolean
  ): Promise<UsersPagedResult> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    if (searchTerm) params.append('searchTerm', searchTerm);
    if (role !== undefined) params.append('role', role.toString());
    if (isActive !== undefined) params.append('isActive', isActive.toString());

    const response = await apiClient.get<UsersPagedResult>(`/api/Users?${params.toString()}`);
    return response as any;
  }

  async getById(id: number): Promise<User> {
    const response = await apiClient.get<User>(`/api/Users/${id}`);
    return response as any;
  }

  async create(data: CreateUserRequest): Promise<User> {
    const response = await apiClient.post<User>('/api/Users', data);
    return response as any;
  }

  async update(id: number, data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<User>(`/api/Users/${id}`, data);
    return response as any;
  }

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/Users/${id}`);
  }

  async toggleActive(id: number): Promise<User> {
    const response = await apiClient.patch<User>(`/api/Users/${id}/toggle-active`);
    return response as any;
  }

  async getStatistics(id: number): Promise<UserStatistics> {
    const response = await apiClient.get<UserStatistics>(`/api/Users/${id}/statistics`);
    return response as any;
  }
}

export const usersService = new UsersService();
